<template>
  <v-row class="mb-8">
    <v-col cols="12">
      <Subtitle
        :text="t$('.participation_circle')"
        :font-size="1.2"
        :font-weight="900"
      />
    </v-col>
    <v-col cols="12">
      <GroupSelect
        :org-level="orgLevel"
        :org-level-options="memberScope"
        :org-level-label="t$('.participation_circle')"
        :queryParams="{ calendar: true }"
        :group="group"
        :group-label="t$('.group')"
        @groupChange="group = $event"
        :is-edit="isEdit"
        :is-group-page="isGroupPage"
        @orgLevelChange="orgLevel = $event"
      />
    </v-col>
    <v-col cols="12">
      <div
        v-if="onlyConfidentialAvailable"
        style="margin-bottom: 16px; font-style: italic"
      >
        Diese Gruppe ist vertraulich. Nur Mitglieder können auf diese
        Veranstaltung zugreifen.
      </div>
      <ContentfulSelect
        v-else
        :options="attendOptions"
        v-model="memberAccess"
        item-value="type"
        :label="t$('.access_and_visibility')"
      />
      <v-switch class="mt-0" v-model="allowGuests" inset>
        <template v-slot:label>
          <TextBody :text="t$('.allow_participation_with_guests')" large />
        </template>
      </v-switch>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  Ref,
  ref,
  watch,
  WritableComputedRef,
} from "vue"
import useTranslate from "features/common/translate/concerns/useTranslate"
import EventFormService from "features/events/form/event-form.service"
import Subtitle from "components/UI/Subtitle.vue"
import MemberService, { OrgLevel } from "features/member/member.service"
import GroupContext, { useGroupContext } from "features/group/groupContext"
import { Group } from "suus-api"
import ContentfulSelect, {
  ContentfulOption,
} from "components/UI/ContentfulSelect/index.vue"
import TextBody from "components/UI/TextBody.vue"
import GroupSelect from "features/common/forms/form-fields/GroupSelect.vue"
import { EventForm } from "components/EventForm/EventForm.vue"
import { Rule } from "features/common/forms/form.service"
import { PanelIdentifier } from "features/events/form/event-form.types"
import { useAccessOptions } from "components/TopicForm/general-fields/use-access-options"
import { GroupAccess } from "components/TopicForm/general-fields/group-access"

export default defineComponent({
  name: "ParticipationCircleFields",
  components: {
    GroupSelect,
    TextBody,
    ContentfulSelect,
    Subtitle,
  },
  setup() {
    const { t$ } = useTranslate("components.event_form")
    const eventFormService: EventFormService<EventForm> =
      inject("EventFormService")
    const memberService: MemberService = inject("MemberService")
    const groupContext: GroupContext = useGroupContext()

    const memberScope = memberService.memberScope

    const selectedScope: Ref<OrgLevel> = ref(null)

    const initOrgLevel = (): OrgLevel => {
      const clientScope: OrgLevel = memberScope.value.find(
        (scope) => scope.type === "Client"
      )

      const currentGroupLevelId =
        group.value?.level_id || groupContext.currentGroup.value?.level_id

      const currentLevel = memberScope.value.find(
        (scope) =>
          scope.type === "Level" && scope.value.id === currentGroupLevelId
      )

      const scope = currentLevel || clientScope

      return scope
    }

    const orgLevel: WritableComputedRef<OrgLevel> = computed({
      get(): OrgLevel {
        if (selectedScope.value) {
          return selectedScope.value
        }
        return initOrgLevel()
      },
      set(value: OrgLevel): void {
        selectedScope.value = value
      },
    })

    const groupRule: Rule<EventForm, "group"> = {
      field: "group",
      message: t$("components.event_form.validate_group").toString(),
      selector: "event-form__group",
      panelIdentifier: PanelIdentifier.GENERAL,
      action: (v) => !!v && !!v.name.length,
    }
    eventFormService.registerRule(groupRule as Rule<EventForm>)

    eventFormService.registerProperty({
      name: "group",
    })

    const group: WritableComputedRef<Group> = computed({
      get(): Group {
        return eventFormService.getFormValue<any>("group").value
      },
      set(value: Group): void {
        eventFormService.setFormValue("group", value)
        eventFormService.validate()
      },
    })

    watch(
      () => groupContext.currentGroup.value,
      (newGroup) => {
        if (!group.value && newGroup) {
          group.value = newGroup
        }
      },
      { immediate: true }
    )

    const attendOptions: Ref<ContentfulOption[]> = useAccessOptions(
      group,
      memberService.groupMemberships,
      computed(() => eventFormService.getFormValue("nonmember_access").value),
      (access) => {
        eventFormService.setFormValue("nonmember_access", access)
      },
      eventFormService.getFormValue("nonmember_access").value // initial value
    )

    watch(attendOptions, (newOptions: ContentfulOption[]) => {
      if (
        newOptions.length === 1 &&
        memberAccess.value !== newOptions[0].type
      ) {
        memberAccess.value = newOptions[0].type
      }
    })

    eventFormService.registerProperty({
      name: "nonmember_access",
    })

    const memberAccess: WritableComputedRef<GroupAccess> = computed({
      get(): GroupAccess {
        return eventFormService.getFormValue("nonmember_access").value
      },
      set(value: GroupAccess): void {
        eventFormService.setFormValue("nonmember_access", value)
      },
    })

    eventFormService.registerProperty({
      name: "allow_guests",
    })
    const allowGuests: WritableComputedRef<boolean> = computed({
      get(): boolean {
        return eventFormService.getFormValue("allow_guests").value ?? false
      },
      set(value: boolean): void {
        eventFormService.setFormValue("allow_guests", value)
      },
    })

    const isGroupPage = computed(() => {
      return !!groupContext.currentGroup.value?.id
    })

    const onlyConfidentialAvailable = computed(() => {
      return (
        attendOptions.value.length === 1 &&
        attendOptions.value[0].type === GroupAccess.CONFIDENTIAL
      )
    })

    return {
      t$,
      eventFormService,
      memberScope,
      orgLevel,
      group,
      attendOptions,
      memberAccess,
      allowGuests,
      isGroupPage,
      isEdit: eventFormService.isEdit,
      onlyConfidentialAvailable,
    }
  },
})
</script>

<style lang="sass">
%font-stack
  font-family: "system-ui" !important
  font-size: 1.2rem !important
  font-weight: 300
.suus__group-select
  @extend %font-stack
  .v-label
    top: 15px
    .v-select__slot
      .v-select__selections
        .v-select__selection
          @extend %font-stack
    .v-select-list
      .v-list-item__title
        @extend %font-stack
        .v-list-item
          border-bottom: 1px solid #e5e5e5 !important
</style>
